import { useEffect, useMemo, useRef, useState } from "react";
import type { AppSummary } from "@compath/renderer";
import { useAuth } from "./AuthProvider";
import { isAdmin } from "./oidc";
import { makeTransport } from "./apiClient";
import type { ActivityView, OpenApp } from "../models/types";

// Orchestration state + handlers for the shell window: which apps exist, which one is
// selected/open, and which top-level section (explore/admin) is active. Extracted out of the
// App view so the view stays a thin render of this controller's state.
export function useShellController() {
  const { status, principal, token } = useAuth();

  // Stable transport that always reads the freshest token via a ref.
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const transport = useMemo(() => makeTransport(() => tokenRef.current()), []);

  const [apps, setApps] = useState<AppSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [open, setOpen] = useState<OpenApp | null>(null);
  const [view, setView] = useState<ActivityView>("explore");
  const [query, setQuery] = useState("");
  const [navOpen, setNavOpen] = useState(false); // sidebar drawer (only visible/toggled on narrow screens)

  const reload = useMemo(() => async () => {
    const list = await transport.apps();
    setApps(list);
    setSelectedId((cur) => cur ?? list[0]?.id ?? null);
  }, [transport]);

  useEffect(() => {
    if (status === "authenticated") void reload().catch(() => setApps([]));
  }, [status, reload]);

  const admin = principal ? isAdmin(principal) : false;
  const selected = apps.find((a) => a.id === selectedId) ?? null;

  function selectApp(id: string) {
    setSelectedId(id);
    setOpen(null);
    setView("explore");
    setNavOpen(false);
  }
  function openApp(app: AppSummary) {
    setOpen({ appId: app.id, screen: app.home ?? `${app.id}/home`, name: app.name });
    setView("explore");
    setNavOpen(false);
  }
  function closeApp() {
    setOpen(null);
  }
  function changeView(v: ActivityView) {
    setView(v);
    setOpen(null);
    setNavOpen(false);
  }
  function goToLink() {
    setView("admin");
    setOpen(null);
  }
  function toggleNav() {
    setNavOpen((o) => !o);
  }
  function closeNav() {
    setNavOpen(false);
  }
  async function onLinked() {
    await reload();
    setView("explore");
  }
  function getToken() {
    return tokenRef.current();
  }

  return {
    status,
    principal,
    admin,
    apps,
    selectedId,
    open,
    view,
    query,
    navOpen,
    selected,
    transport,
    setQuery,
    selectApp,
    openApp,
    closeApp,
    changeView,
    goToLink,
    toggleNav,
    closeNav,
    onLinked,
    getToken,
  };
}
